import React from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import ReusableSearchInput from "../../../../../components/ReusableSearchInput";
import { useManageClientContext } from "../../../../../context/ManageClientContex";
import { createUpdateProjectClientPayloadModel } from "../../../../../model/clientmodel";

interface ProjectFormModel {
  name: string;
  aliasname: string;
  project_code: string;
  start_ts: Date | null;
  end_ts: Date | null;
  manager: string;
  altmanager: string | null;
  project_type: string;
  status: string;
  is_billable: string;
  department: string;
  description: string;
}

const defaultValue: ProjectFormModel = {
  name: "",
  aliasname: "",
  project_code: "",
  start_ts: null,
  end_ts: null,
  manager: "",
  altmanager: "",
  project_type: "",
  status: "",
  is_billable: "",
  department: "",
  description: "",
};

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { id: number, clientId: number, formtype: string } | undefined; // Assuming state is an object with an 'id' property
  // Access 'id' from state if it exists
  const projectID = state?.id;

  const {
    projectOptions,
    projectDetail,
    projectFormType,
    getProjectOption,
    addandupdateClientProject,
    getProjectDetails,
  } = useManageClientContext();
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProjectFormModel>({ mode: "all", defaultValues: defaultValue });

  React.useEffect(() => {
    reset(defaultValue);
    if (!projectOptions) getProjectOption();
    if (state?.formtype === "update" && !projectDetail) {getProjectDetails(Number(projectID));}
    if (projectDetail && state?.formtype === "update") {
      setValue("name", projectDetail.name);
      setValue("aliasname", projectDetail?.aliasname);
      setValue("project_code", projectDetail?.project_code);
      setValue(
        "start_ts",
        moment(projectDetail.start_ts).isValid() ? moment(projectDetail.start_ts).toDate() : null,
      );
      setValue(
        "end_ts",
        moment(projectDetail.end_ts).isValid() ? moment(projectDetail.end_ts).toDate() : null,
      );
      setValue("manager", projectDetail.manager);
      setValue("altmanager", projectDetail.altmanager);
      setValue("project_type", projectDetail.project_type_id.toString());
      setValue("status", projectDetail["status-id"].toString());
      setValue("is_billable", projectDetail.is_billable ? "Yes" : "No");
      setValue("department", projectDetail["dept-id"].toString());
      setValue("description", projectDetail.description);
    }
  }, [state?.formtype, reset, getProjectOption, projectDetail, setValue]);

  const onSubmit: SubmitHandler<ProjectFormModel> = async (data) => {
    const projectPayload: createUpdateProjectClientPayloadModel = {
      id: projectFormType === "update" ? projectDetail?.id : null,
      name: data.name,
      description: data.description,
      clients_id: Number(state?.clientId),
      project_stages_id: Number(data.status),
      start_ts: moment(data.start_ts).isValid() ? moment(data.start_ts).format("YYYY-MM-DD HH:mm:ss.SSS") : null,
      end_ts: moment(data.end_ts).isValid() ? moment(data.end_ts).format("YYYY-MM-DD HH:mm:ss.SSS") : null,
      last_send_ts: null,
      is_enabled: true,
      is_billable: data.is_billable === "Yes" ? true : false,
      project_type_id: Number(data.project_type),
      department_id: Number(data.department),
      manager: data.manager,
      aliasname: data.aliasname,
      project_code: data.project_code,
      altmanager: data.altmanager,
      p_location: "",
    };
    await addandupdateClientProject(projectPayload);
  };

  return (
    <form
      className='max-w-4xl grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 '
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className='mb-2 '>
        <label htmlFor='projectName' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Project Name <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='name'
          control={control}
          rules={{ required: "Project Name is required" }}
          render={({ field }) => (
            <input
              {...field}
              type='text'
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            />
          )}
        />
        {errors.name && <p className='text-red-500 text-sm mt-1'>{errors.name.message}</p>}
      </div>

      <div className='mb-2 '>
        <label htmlFor='aliasName' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Alias Name <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='aliasname'
          control={control}
          rules={{ required: "Alias Name is required" }}
          render={({ field }) => (
            <input
              {...field}
              type='text'
              maxLength={10}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            />
          )}
        />
        {errors.aliasname && <p className='text-red-500 text-sm mt-1'>{errors.aliasname.message}</p>}
      </div>

      <div className='mb-2 '>
        <label htmlFor='Code' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Project Code <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='project_code'
          control={control}
          rules={{ required: "Project Code is required" }}
          render={({ field }) => (
            <input
              {...field}
              type='text'
              maxLength={10}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            />
          )}
        />
        {errors.project_code && <p className='text-red-500 text-sm mt-1'>{errors.project_code.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='startDate' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Start Date <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='start_ts'
          control={control}
          rules={{ required: "Start Date is required" }}
          render={({ field }) => (
            <CustomDatePicker
              onChange={field.onChange}
              selectedDate={field.value}
              isClearable={false}
              dateFormat='dd/MM/yyyy'
            />
          )}
        />
        {errors.start_ts && <p className='text-red-500 text-sm mt-1'>{errors.start_ts.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='endDate' className='block mb-2  text-sm font-medium text-gray-900 dark:text-white'>
          End Date
        </label>
        <Controller
          name='end_ts'
          control={control}
          render={({ field }) => (
            <CustomDatePicker
              onChange={field.onChange}
              selectedDate={field.value}
              isClearable={false}
              dateFormat='dd/MM/yyyy'
            />
          )}
        />
        {errors.end_ts && <p className='text-red-500 text-sm mt-1'>{errors.end_ts.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='manager' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Manager <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='manager'
          control={control}
          defaultValue=''
          rules={{ required: "Manager is required" }}
          render={({ field }) => (
            <ReusableSearchInput
              value={field.value ?? ""}
              onChange={field.onChange}
              setOptionsData={(item) => setValue("manager", item?.ldap_uid ?? "")}
            />
          )}
        />
        {errors.manager && <p className='text-red-500 text-sm mt-1'>{errors.manager.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='altManager' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Alt. Manager
        </label>
        <Controller
          name='altmanager'
          control={control}
          defaultValue=''
          render={({ field }) => (
            <ReusableSearchInput
              value={field.value ?? ""}
              onChange={field.onChange}
              setOptionsData={(item) => setValue("altmanager", item?.ldap_uid ?? "")}
            />
          )}
        />
      </div>

      <div className='mb-2'>
        <label htmlFor='status' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Project Type <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='project_type'
          control={control}
          defaultValue=''
          rules={{ required: "Project type is required" }}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            >
              <option selected>Select</option>
              {projectOptions?.["project types"].map((item) => (
                <option key={item.id} value={item.id}>
                  {item.project_type}
                </option>
              ))}
            </select>
          )}
        />
        {errors.status && <p className='text-red-500 text-sm mt-1'>{errors.status.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='billing' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Billing <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='is_billable'
          control={control}
          defaultValue=''
          rules={{ required: "Billing is required" }}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            >
              <option selected>Select</option>
              <option value='Yes'>Yes</option>
              <option value='No'>No</option>
            </select>
          )}
        />
        {errors.is_billable && <p className='text-red-500 text-sm mt-1'>{errors.is_billable.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='status' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Status <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='status'
          control={control}
          defaultValue=''
          rules={{ required: "Status is required" }}
          render={({ field }) => (
            <select
              id='status'
              value={field.value}
              onChange={field.onChange}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            >
              <option selected>Select</option>
              {projectOptions?.["proj status"].map((item) => (
                <option key={item.id} value={item.id}>
                  {item.display}
                </option>
              ))}
            </select>
          )}
        />
        {errors.status && <p className='text-red-500 text-sm mt-1'>{errors.status.message}</p>}
      </div>

      <div className='mb-2'>
        <label htmlFor='department' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Department <span className='text-red-400'>*</span>
        </label>
        <Controller
          name='department'
          control={control}
          rules={{ required: "Department is required" }}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            >
              <option selected>Select</option>
              {projectOptions?.departments.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.department}
                </option>
              ))}
            </select>
          )}
        />
        {errors.department && <p className='text-red-500 text-sm mt-1'>{errors.department.message}</p>}
      </div>

      <div className='mb-2 col-span-full'>
        <label htmlFor='description' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Description
        </label>
        <Controller
          name='description'
          control={control}
          defaultValue=''
          render={({ field }) => (
            <textarea
              {...field}
              value={field.value}
              rows={4}
              maxLength={200}
              id='description'
              className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
            />
          )}
        />
      </div>
      <div className='max-w-4xl'>
        <button
          type='submit'
          className='text-white bg-blue-700 mr-2 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
        >
          Save
        </button>
        <button
          type='button'
          onClick={() => navigate(-1)}
          className='text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800'
        >
          Close
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;
